import { cn } from './cn';

import type { HTMLAttributes, ReactNode } from 'react';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Adds a spring-like hover lift — use for clickable cards / grid tiles. */
  interactive?: boolean;
}

export function Card({ className, interactive, ...rest }: CardProps) {
  return (
    <div
      className={cn(
        // A hair of vertical gradient + ring + layered shadow reads as a real
        // material instead of a flat outlined box.
        'rounded-card bg-surface [background-image:linear-gradient(180deg,#ffffff_0%,#fbfbfe_100%)]',
        'ring-1 ring-hairline shadow-e2',
        'transition-[box-shadow,transform] duration-200 ease-out',
        interactive && 'cursor-pointer hover:-translate-y-0.5 hover:shadow-e3',
        className,
      )}
      {...rest}
    />
  );
}

interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

export function CardHeader({
  title,
  description,
  action,
  className,
  children,
  ...rest
}: CardHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 border-b border-border px-5 py-4',
        className,
      )}
      {...rest}
    >
      {children ?? (
        <div className="flex flex-col gap-1">
          {title && <h3 className="text-base font-semibold leading-tight">{title}</h3>}
          {description && <p className="text-sm text-foreground-muted">{description}</p>}
        </div>
      )}
      {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
    </div>
  );
}

export function CardContent({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-5', className)} {...rest} />;
}

export function CardFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 border-t border-border bg-surface-muted/40 px-5 py-3',
        className,
      )}
      {...rest}
    />
  );
}
